import { FC, useEffect, useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectQuiz, selectIsQuizStarted, selectIsQuizEnded } from 'store/quiz/quizSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
// Components
import Countdown from 'components/Countdown';
// Utilities
import { getTimeRemaining } from 'utilities/Utilities';
// i18next
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

let countdownInterval:any;
const milliseconds = 1000;

let now = dayjs();

const QuizCountdown:FC = () => {
  const { t } = useTranslation();
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const isQuizStarted:boolean | undefined = useAppSelector(selectIsQuizStarted);
  const isQuizEnded:boolean | undefined = useAppSelector(selectIsQuizEnded);

  const startDate = useMemo(() => {
    if ( !quiz ) return null;
    return quiz.startsDate ? dayjs(Number(quiz.startsDate)) : null;
    // eslint-disable-next-line
  }, []);
  const endDate = useMemo(() => {
    if ( !quiz ) return null;
    return quiz.endsDate ? dayjs(Number(quiz.endsDate)) : null;
    // eslint-disable-next-line
  }, []);

  const getPrefix = () => {
    if ( !quiz ) return '';
    if ( quiz.type === QuizTypes.Prediction ) return 'prediction_';
    if ( [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type) ) return 'poll_';
    if ( quiz.type === QuizTypes.Survey ) return 'survey_';
    return '';
  }

  const [ startCountdown, setStartCountdown ] = useState(false);
  const [ finalDate, setFinalDate ] = useState<Dayjs | null>(null);
  const [ label, setLabel ] = useState(`${t('components.countdown.quizNotYetStarted')}`);
  const [ isInitialized, setIsInitialized ] = useState(false);

  const [ days, setDays ] = useState(0);
  const [ hours, setHours ] = useState(0);
  const [ minutes, setMinutes ] = useState(0);

  useEffect(() => {
    if (isInitialized) {
      setIsInitialized(false);
    }

    if ( typeof isQuizStarted === 'undefined' ) {
      setIsInitialized(true);
      setLabel(t('components.countdown.quizNotYetStarted'))
      return;
    }

    if ( !quiz ) return;
    if ( !quiz.questions || !quiz.questions.length ) return;

    if ( !isQuizStarted ){
      setLabel(t(`components.countdown.${getPrefix()}quizStarts`));
      setFinalDate(startDate);
      setStartCountdown(true);
    } else if ( isQuizEnded ){
      setLabel(t(`components.countdown.${getPrefix()}quizEnded`));
      setIsInitialized(true);
    } else {
      setLabel(t(`components.countdown.${getPrefix()}quizEndsIn`));
      if ( endDate ){
        setStartCountdown(true);
        setFinalDate(endDate);
      } else {
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    if ( quiz && quiz.type === QuizTypes.Personality ) {
      setIsInitialized(true);
      return;
    }

    if ( startCountdown ){
      countdownInterval = setInterval(() => {
        now = dayjs();
        if ( finalDate ){
          if ( now.isAfter(finalDate) ){
            setLabel(t(`components.countdown.${getPrefix()}quizEnded`));
            clearInterval(countdownInterval);
          } else {
            const remaining = getTimeRemaining(now, finalDate);

            setDays(remaining.days);
            setHours(remaining.hours);
            setMinutes(remaining.minutes);
          }
          setIsInitialized(true);
        }
      }, milliseconds);
    }
    // eslint-disable-next-line
  }, [startCountdown, i18n.language]);

  const hideNumbers = isQuizStarted === undefined || isQuizEnded;

  // if ( !startCountdown ) return null;
  if (isQuizStarted && !quiz?.endsDate) return null;
  if ( quiz && quiz.type === QuizTypes.Personality ) {
    const label = !isQuizStarted && !isQuizEnded
      ? t('components.countdown.quizNotYetStarted')
      : isQuizEnded
        ? t('components.countdown.finished')
        : '';
    
    return label ? (
      <Countdown
        label={label}
        isInitialized={isInitialized}
        hideNumbers={true}
        days={days}
        hours={hours}
        minutes={minutes}
      />
    ) : null
  }
  return (
    <Countdown
      label={label}
      isInitialized={isInitialized}
      hideNumbers={hideNumbers}
      days={days}
      hours={hours}
      minutes={minutes}
    />
  )
}

export default QuizCountdown;
